import { useEffect, useState, useImperativeHandle, forwardRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import Image from 'pages/Customer/CustomerDetails/EditView/DragAndDrop/components/Image'
import ReAuthModal from './ReAuthModal'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import {
  clearUnmaskedCard,
  refreshCardImage,
  showImagePreview
} from 'slices/customer'
import AlertModal from './AlertModal'
import { Lock } from '@mui/icons-material'
import { api } from 'lib/axios'
import { buildVaultPreviewPayloadFromUnmasked } from 'utils/vaultPreviewSnapshot'

const isAbsoluteUrl = value =>
  typeof value === 'string' && /^(https?:)?\/\//i.test(value)

const MaskedImage = forwardRef(
  (
    {
      url,
      item,
      isMasked: initialMasked = true,
      maskIntensity = 15,
      width = '100%',
      height = '100%',
      alt,
      cardId: propCardId,
      isCardTab = false,
      refreshKey = null,
      onMaskStateChange,
      imageIndex = 0,
      disableVisualMask = false,
      showRedactFail = false,
      redactFailMessage = '',
      relockWhenUnmaskCleared = false,
      ...props
    },
    ref
  ) => {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const dispatch = useDispatch()
    const { imagePreview } = useSelector(state => state.customer)
    const unmaskedCards = useSelector(
      state => state.customer.unmaskedCards || {}
    )
    const cardId = propCardId || imagePreview.cardId
    const unmaskedCardData = cardId ? unmaskedCards[cardId] : null
    const [isMasked, setIsMasked] = useState(initialMasked)
    const [authDialogOpen, setAuthDialogOpen] = useState(false)
    const [displayUrl, setDisplayUrl] = useState(url)
    const [isLoading, setIsLoading] = useState(false)
    const [alertOpen, setAlertOpen] = useState(false)
    const [alertMessage, setAlertMessage] = useState('')

    const effectiveCanSeeLock = !disableVisualMask

    useEffect(() => {
      setDisplayUrl(url)
      setIsMasked(initialMasked)
      setAuthDialogOpen(false)
      setIsLoading(false)
    }, [url, initialMasked])

    useEffect(() => {
      if (unmaskedCardData && cardId && isMasked) {
        if (unmaskedCardData.images && Array.isArray(unmaskedCardData.images)) {
          const matchingImage = unmaskedCardData.images.find(
            img => img.index === imageIndex + 1
          )
          if (matchingImage?.url) {
            setDisplayUrl(matchingImage.url)
            setIsMasked(false)
            return
          }
        }

        if (unmaskedCardData.imageUrl) {
          setDisplayUrl(unmaskedCardData.imageUrl)
          setIsMasked(false)
        }
      }
    }, [unmaskedCardData, cardId, imageIndex, isMasked])

    useEffect(() => {
      if (relockWhenUnmaskCleared && !unmaskedCardData && !isMasked) {
        setDisplayUrl(url)
        setIsMasked(true)
      }
    }, [relockWhenUnmaskCleared, unmaskedCardData, url, isMasked])

    useEffect(() => {
      if (typeof onMaskStateChange === 'function') {
        onMaskStateChange(isMasked)
      }
    }, [isMasked, onMaskStateChange])

    const handleToggleClick = e => {
      e.stopPropagation()
      if (isMasked) {
        setAuthDialogOpen(true)
      } else {
        if (cardId) {
          dispatch(clearUnmaskedCard({ cardId }))
        }
        setDisplayUrl(url)
        setIsMasked(true)
        if (cardId) {
          dispatch(refreshCardImage({ cardId }))
        }
      }
    }

    useImperativeHandle(ref, () => ({
      handleToggleClick
    }))

    const handleCloseAuthDialog = () => {
      if (!isSubmitting) {
        setAuthDialogOpen(false)
      }
    }


    const openSystemPreview = () => {
      const fileName =
        item?.fileName ||
        item?.name ||
        item?.file_obj?.name ||
        item?.file_obj?.fileName ||
        alt ||
        'Card image'

      const docId =
        url ||
        item?.document_revision_id ||
        item?.file_obj?.document_revision_id ||
        item?.id

      if (!docId) {
        console.warn('MaskedImage: Missing docId, cannot open preview')
        return
      }

      const documentType =
        item?.gb_doc_type ||
        item?.file_obj?.gb_doc_type ||
        item?.documentType ||
        null

      const isNewRelation =
        item?.new_relation_addition ||
        item?.file_obj?.new_relation_addition ||
        false

      const vaultPreviewPayload = buildVaultPreviewPayloadFromUnmasked(
        unmaskedCards,
        cardId
      )

      dispatch(
        showImagePreview({
          name: fileName,
          docId,
          preview: true,
          documentType,
          cardId,
          imageIndex,
          isNewRelation,
          ...vaultPreviewPayload
        })
      )
    }

    const handleAuthConfirm = async ({ email, password }) => {
      try {
        setIsSubmitting(true)
        setIsLoading(true)
        const response = await api.post(
          '/Api/V8/custom/customer/validate-user',
          { email, password }
        )
        const result = response?.data
        if (result?.success === true) {
          setAuthDialogOpen(false)
          // setIsMasked(false)
          openSystemPreview()
        } else {
          setAlertMessage(result?.message || 'Invalid user')
          setAlertOpen(true)
        }
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.errors?.detail ||
          'Failed to validate user'
        setAlertMessage(message)
        setAlertOpen(true)
        console.error('Error validating user:', error)
      } finally {
        setIsSubmitting(false)
        setIsLoading(false)
      }
    }

    return (
      <>
        <MaskedImageContainer>
          <ImageWrapper
            isMasked={isMasked && effectiveCanSeeLock}
            maskIntensity={maskIntensity}
            canSeeLock={effectiveCanSeeLock}
            isLoading={isLoading}
          >
            {isLoading ? (
              <LoadingOverlay>
                <CircularProgress size={40} sx={{ color: '#F6C44B' }} />
              </LoadingOverlay>
            ) : (
              <Image
                url={displayUrl}
                item={item}
                width={width}
                height={height}
                rawUrl={isAbsoluteUrl(displayUrl)}
                shouldMask={isMasked}
                cardId={cardId}
                alt={alt}
                imageIndex={imageIndex}
                refreshKey={refreshKey}
                {...props}
              />
            )}

            {isMasked && effectiveCanSeeLock && (cardId || isCardTab) && (
              <LockedOverlay
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                }}
                onMouseDown={e => e.stopPropagation()}
              >
                <Lock className="lock-icon" />
                <span className="lock-text">Restricted</span>
                {showRedactFail && (
                  <span className="redact-fail-text">
                    {redactFailMessage?.trim()}
                  </span>
                )}
              </LockedOverlay>
            )}
          </ImageWrapper>
        </MaskedImageContainer>

        <ReAuthModal
          open={authDialogOpen}
          onClose={handleCloseAuthDialog}
          onConfirm={handleAuthConfirm}
          isSubmitting={isSubmitting}
          title="Enter your portal credentials to view details"
        />
        <AlertModal
          open={alertOpen}
          onClose={() => setAlertOpen(false)}
          message={alertMessage}
          title="Error"
        />
      </>
    )
  }
)

const MaskedImageContainer = styled.div`
  position: relative !important;
  width: 100%;
  height: 100%;
  display: block;
`

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: ${props => (props.isLoading ? '100%' : 'auto')};

  img {
    filter: ${props =>
    props.isMasked && props.canSeeLock
      ? `blur(${props.maskIntensity}px)`
      : 'none'} !important;
    transition: filter 0.3s ease-in-out;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props =>
    props.isMasked && props.canSeeLock
      ? 'rgba(0, 0, 0, 0.3)'
      : 'transparent'};
    pointer-events: none;
    transition: background 0.3s ease-in-out;
    z-index: 1;
  }
`

const LockedOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.4);
  cursor: default;
  color: white;
  pointer-events: auto;
  user-select: none;

  .lock-icon {
    font-size: 1.5rem !important;
    margin-bottom: 4px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
  }

  .lock-text {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }

  .redact-fail-text {
    margin-top: 6px;
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }
`

const LoadingOverlay = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`
export default MaskedImage